import { Storage } from "@/services/Storage";
import axios from "axios";
import { Device } from "@/services/Device";
import { hardwareBridge } from '@/services/hardwareBridge';
import { responses } from '@/utils/responses';

const LOCAL_BACKOFFICE_INDEX = "C:\\Users\\Felix\\WebstormProjects\\lytningPOs\\Online BackOffice Front - End - Copy\\dist\\index.html";

const buildFileUrl = (windowsPath) => {
    if (!windowsPath) return null;
    const normalized = windowsPath.replace(/\\/g, '/');
    return encodeURI(`file:///${normalized}`);
};

const tryOpenExternal = async (url) => {
    if (!url || !window?.electronBridge?.openExternal) return false;
    try {
        const result = await window.electronBridge.openExternal(url);
        return result?.ok !== false;
    } catch (error) {
        return false;
    }
};

export default {
    name: 'backOffice',
    state: {},
    getters: {},
    mutations: {},
    actions: {
        openOnlineBackOffice: async (context, page = { name: `stock` }) => {
            const isLinux = navigator.userAgent.toLowerCase().includes('linux') ||
                (window?.process?.versions?.electron && process.platform === 'linux');
            const isElectron = !!(window?.hardwareIPC || window?.electronBridge || window?.process?.versions?.electron);
            const localFileUrl = buildFileUrl(LOCAL_BACKOFFICE_INDEX);

            const pageName = (page && page.name) || 'stock';
            console.log(`[deeplink][openOnlineBackOffice] invoked page=${pageName} isElectron=${isElectron}`);

            let store = null;
            let session = null;
            try {
                store = await Storage.getDefaultStore();
                console.log(`[deeplink][openOnlineBackOffice] store=${store && store.StoreDB ? store.StoreDB : 'undefined'} storeName=${store && store.StoreName ? store.StoreName : 'undefined'}`);
                if (!store || !store.StoreDB) {
                    throw new Error('No default store configured');
                }
                session = await Storage.getCurrentSession(store.StoreDB);
                console.log('[deeplink][openOnlineBackOffice] session=' + JSON.stringify(session) + ` expectedStoreDb=${store.StoreDB}`);
                if (!session || !session.sessionId) {
                    throw new Error('No deeplink session for this store');
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('[deeplink][openOnlineBackOffice] session open failed - opening bare URL', e);
                console.warn(`[deeplink][openOnlineBackOffice] falling back to bare URL because: ${e && e.message ? e.message : e}`);
                try {
                    if (isElectron) {
                        await tryOpenExternal('LytningCore://open');
                    } else {
                        window.open('LytningCore://open', '_blank');
                    }
                } catch (_) { /* best-effort */ }
                return { ok: false, reason: 'no-session' };
            }

            // sessionId is opaque; the receiver fetches user / store
            // details itself. The plaintext password NEVER leaves this
            // process.
            const protocolUrl = `LytningCore://open?session=${encodeURIComponent(session.sessionId)}`
                              + `&store=${encodeURIComponent(store.StoreDB)}`
                              + `&page=${encodeURIComponent(pageName)}`;

            console.log('[deeplink][openOnlineBackOffice] opening session URL', {
                store: store.StoreDB,
                page: pageName,
                sessionIdPrefix: session.sessionId.slice(0, 8) + '...'
            });
            console.log('[openOnlineBackOffice] Opening BackOffice with deep link...');
            console.log('[openOnlineBackOffice] Store:', store.StoreName);

            try {
                if (isElectron) {
                    // Method 1: Write to shared file for BackOffice's file watcher (most reliable)
                    // This is the PRIMARY method on Linux since openExternal silently fails.
                    // The shared file now carries the session URL (same shape as the protocol
                    // URL) instead of a base64-encoded user blob.
                    try {
                        if (window?.electronBridge?.hardwareInvoke) {
                            const writeResult = await window.electronBridge.hardwareInvoke('backoffice:writeAuthFile', { protocolUrl });
                            console.log('[deeplink][openOnlineBackOffice] auth file written ok=' + !!(writeResult && writeResult.ok !== false), writeResult);
                            console.log('[openOnlineBackOffice] Auth file written successfully');

                            // On Linux, auth file + launch the app so it can pick up the auth file
                            if (isLinux) {
                                console.log('[openOnlineBackOffice] Linux detected, launching Lytning Core...');

                                try {
                                    const launchResult = await window.electronBridge.hardwareInvoke('backoffice:launchApp', {});
                                    console.log('[deeplink][openOnlineBackOffice] linux launch result', launchResult);
                                    console.log('[openOnlineBackOffice] Launch result:', launchResult);
                                    // Surface install-missing / launch failure in plain language (Ticket #22077)
                                    if (launchResult && launchResult.ok === false) {
                                        const msg = launchResult.error
                                            || 'Lyt-ning Back Office could not be opened. Install Lyt-ning Core, then try again.';
                                        responses.showError(msg);
                                        return { ok: false, source: 'auth-file', launch: launchResult };
                                    }
                                    // Confirm launch so cashiers know Core is opening even if the window is slow
                                    responses.showInfo('Back Office opened in a new window');
                                } catch (launchErr) {
                                    console.warn('[openOnlineBackOffice] Failed to launch app:', launchErr);
                                    responses.showError('Lyt-ning Back Office could not be opened. Install Lyt-ning Core, then try again.');
                                    return { ok: false, source: 'auth-file', launchError: String(launchErr && launchErr.message ? launchErr.message : launchErr) };
                                }

                                return { ok: true, source: 'auth-file' };
                            }
                        }
                    } catch (fileErr) {
                        console.warn('[openOnlineBackOffice] Failed to write auth file:', fileErr);
                    }

                    // Method 2: Try to open via protocol (Windows only - silently fails on Linux)
                    if (!isLinux) {
                        console.log('[openOnlineBackOffice] Opening protocol URL:', protocolUrl.substring(0, 80) + '...');
                        const openedByProtocol = await tryOpenExternal(protocolUrl);
                        if (openedByProtocol) {
                            return { ok: true, source: 'protocol' };
                        }
                    }

                    if (localFileUrl && !isLinux) {
                        const openedByFile = await tryOpenExternal(localFileUrl);
                        if (openedByFile) {
                            return { ok: true, source: 'local-file' };
                        }
                    }
                }
            } catch (e) {
                console.warn('openOnlineBackOffice deep link failed, falling back to web URL:', e);
            }

            const storeLink = 'https://apps.microsoft.com/detail/XP9MDB2QR2SPZM?hl=en-US&gl=ZM&ocid=pdpshare';
            const openedStore = await tryOpenExternal(storeLink);
            if (!openedStore) {
                window.open(storeLink, '_blank');
            }
            return { ok: false, source: 'store-fallback' };

        },
        searchExpenseAccounts: async (context, accountName) => {
            let store = await Storage.getDefaultStore();
            return axios.get(backOfficeTomcatApi + `chartOfAccounts/${store.StoreDB}/searchExpenseAccounts?accountName=` + accountName)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err));
        },
        capturePayout: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let pcName = (window?.hardwareIPC || window?.electronBridge) ? await hardwareBridge.getPcName() : await Device.getPCName();

            payload.User = user.Username;
            payload.PC = pcName;
            return axios.post(backOfficeTomcatApi + `Payments/${store.StoreDB}/storePayout`, payload)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err));
        },
        captureCashDrop: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            payload.user = user.Username;
            payload.pc = settings.PC;
            return axios.post(backOfficeTomcatApi + `safeFunctions/${store.StoreDB}/cashierDepositToSafe`, payload)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err));
        },
        getCashSafes: async () => {
            let store = await Storage.getDefaultStore();
            return axios.get(backOfficeTomcatApi + `chartOfAccounts/${store.StoreDB}/getSafeByType?type=CASH`)
                .then(({ data }) => data).catch(err => errorHandler.tomcatError(err))
        }
    }
}
